import React, { useEffect, useMemo, useState } from "react";
import groupBy from "lodash/groupBy";

import useDialog from "fe-shared/src/hooks/useDialog";

import { Transaction } from "../../types";

import OverrideRegYearButton from "./components/OverrideRegYearButton";
import OverrideRegYearDialog from "./components/OverrideRegYearDialog";
import OverrideRegYearWarningDialog from "./components/OverrideRegYearWarningDialog";
import { TransactionType } from "./../../types";
import { getAvailableRegYears } from "../../helpers/regulatoryYear";

import styles from "./OverrideRegYear.module.scss";

enum OverrideRegulatoryYearDialogType {
  None = "None",
  Warning = "Warning",
  Edit = "Edit"
}

interface KeyboardEvent {
  key: string;
}

interface Props {
  transaction: Transaction;
}

const OverrideRegYear = ({ transaction }: Props) => {
  const {
    transactionId,
    traversedMaterials,
    isCompletelyInitialDelivery = false
  } = transaction;
  const regYears: string[] = useMemo(
    () => Object.keys(groupBy(traversedMaterials, "regYear")),
    [traversedMaterials]
  );

  const [activeDialog, setActiveDialog] = useState<OverrideRegulatoryYearDialogType>(OverrideRegulatoryYearDialogType.None);

  const isWarningActive = activeDialog === OverrideRegulatoryYearDialogType.Warning;
  const isEditActive = activeDialog === OverrideRegulatoryYearDialogType.Edit;

  const currentYear = new Date().getFullYear();

  const overridableRegYears = useMemo(
    () => getAvailableRegYears(currentYear),
    [currentYear]
  ).map(x => x.toString());

  const isAllowedType = [
    TransactionType.Delivery,
    TransactionType.Receipt
  ].includes(transaction.transactionType);

  const hasTracersedRecords = transaction.traversedMaterials && transaction.traversedMaterials?.length > 0;

  const isDisposition = transaction.transactionType == TransactionType.Disposition;

  const isAllowed = hasTracersedRecords &&
    ((regYears.length > 1 && isDisposition) || isAllowedType);

  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isDialogActive) closeDialog();
    };

    if (isDialogActive) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (isDialogActive) {
        window.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [closeDialog, isDialogActive]);

  const onButtonClick = () => {
    if (isAllowedType && !isCompletelyInitialDelivery) {
      setActiveDialog(OverrideRegulatoryYearDialogType.Warning);
      openDialog();
    }
    else if (isCompletelyInitialDelivery || isDisposition) {
      setActiveDialog(OverrideRegulatoryYearDialogType.Edit);
      openDialog();
    }
  };

  return (
    <>
      {isAllowed && (
        <div className={styles.overrideRegYearContainer}>
          <OverrideRegYearButton onClickAction={onButtonClick} />
        </div>
      )}
      {isDialogRendered && isEditActive && (
        <OverrideRegYearDialog
          transactionId={transactionId}
          isActive={isDialogActive && isEditActive}
          onClose={closeDialog}
          availableRegYears={overridableRegYears}
        />
      )}
      {isDialogRendered && isWarningActive && (
        <OverrideRegYearWarningDialog
          isActive={isDialogActive && isWarningActive}
          onClose={closeDialog}
        />
      )}
    </>
  );
};

export default OverrideRegYear;
